'use client';

import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import { Box, Stack, Theme, Typography } from '@mui/material';
import { ReactElement, useState } from 'react';
import InlinePlayer from './InlinePlayer';

/**
 * A capture can be present or absent. Under the biometric model a face check can complete while
 * the ID portrait fails to scan across, which is the whole of what `In progress` means. The
 * absent slot has to render as visibly empty rather than collapse, or the surrounding evidence
 * view cannot answer the question it exists to answer: which input is missing.
 *
 * There is no capture-image service, so no intrinsic image dimensions are available at runtime.
 * A capture therefore takes a `src` string rather than a pre-sized `children` node: the stage
 * fits it with CSS (a fixed box plus `object-fit: contain`) instead of the caller computing a
 * fitted size and sizing the `<img>` to match. That is one calculation, not two.
 */
export type TCapture =
  | {
      kind: 'capture';
      /** The capture image. Shrunk to fit the stage box; never enlarged, never cropped. */
      src: string;
      /** Presents the capture on black, for video stills. */
      onDark?: boolean;
      /** Offer inline playback. Omit for stills. */
      videoSrc?: string;
    }
  | {
      kind: 'missing';
    };

export type TCaptureEntry = {
  id: string;
  label: string;
  /** The stage's target box, in pixels. Every capture gets its own: a face check and an ID
   * portrait are routinely different shapes (a portrait is always a tall rectangle; a liveness
   * capture can be tall or landscape, and often far denser than the portrait), so there is no
   * single box every capture can share. The box also fixes the stage's footprint regardless of
   * what is showing in it, so a missing slot holds it exactly and a poster-to-video swap never
   * shifts the row. */
  box: { width: number; height: number };
  capture: TCapture;
};

export type TCaptureViewerProps = {
  captures: TCaptureEntry[];
  /** Why the captures look the way they do, stated once for the whole set. */
  note?: string;
  /**
   * Called when a capture's image or video fails to load, e.g. an expired or 404 URL. This
   * package has no logging utility and must not grow one: this callback is the only way such a
   * failure reaches the surrounding app. The visible failure state renders regardless of whether
   * a callback is supplied.
   */
  onCaptureError?: (id: string) => void;
};

/**
 * What an absent capture looks like: the slot it would have filled, holding a sentence.
 *
 * **Restores the prototype's "Not yet available", narrowing rather than reversing Charles's 7
 * August direction to use a dash here.** That direction was right for the surface as a whole: a
 * timestamp, a similarity score and a liveness determination sitting side by side each saying
 * "not available" in full words read as noise, and the dashed border already carries "nothing is
 * here" once. This slot is the one the direction did not have in view: the missing thing here
 * *is* the picture, not a field beside it, so there is no neighbouring dash for the sentence to
 * repeat, and the frozen prototype (not silent about this slot, unlike the fields the dash still
 * covers) settles it back to a sentence. See verifime-adr#264.
 *
 * **`CaptureLoadFailed` is unaffected and keeps its own wording.** It is a different fact: a
 * capture that was attached but whose URL no longer resolves, not one that was never taken.
 * Showing the same words for both would tell a reviewer evidence never existed when it did, which
 * is the wrong direction to mislead in.
 */
function MissingCaptureStage({
  width,
  height,
}: Readonly<{ width: number; height: number }>): ReactElement {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={(t: Theme) => ({
        width,
        height,
        // Dashed rather than filled: a filled grey box reads as a capture that is loading, and
        // this one is not coming until somebody attaches it.
        border: `${t.semantic.spacing['3xs']} dashed ${t.palette.divider}`,
        borderRadius: t.semantic.borderRadius.sm,
        backgroundColor: t.palette.background.default,
        padding: t.semantic.spacing.xs,
      })}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{ textWrap: 'pretty' }}
      >
        Not yet available
      </Typography>
    </Stack>
  );
}

/**
 * What a capture looks like once its own image has failed to load. Worded differently from the
 * missing-capture placeholder above on purpose: the two mean different things to a reviewer.
 * Missing means the capture was never attached. This means it was attached and the URL no longer
 * resolves (expired, 404, moved). Showing a dash for both would tell a reviewer evidence never
 * existed when it did, which is the wrong direction to mislead in.
 */
function CaptureLoadFailed({ onDark }: Readonly<{ onDark?: boolean }>): ReactElement {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={(t: Theme) => ({
        width: '100%',
        height: '100%',
        gap: t.semantic.spacing['3xs'],
        padding: t.semantic.spacing.xs,
        color: onDark ? t.palette.common.white : t.palette.text.secondary,
      })}
    >
      <ImageNotSupportedRoundedIcon fontSize="small" aria-hidden sx={{ color: 'inherit' }} />
      <Typography variant="caption" align="center" sx={{ color: 'inherit', textWrap: 'pretty' }}>
        Capture failed to load
      </Typography>
    </Stack>
  );
}

/**
 * An `<img>` that fills its stage box exactly via `object-fit: contain` (shrunk to fit, never
 * enlarged, never cropped), and swaps to a visible failure state if the URL fails to resolve.
 * Shared between a still-only capture and a video's poster frame, so both get the same failure
 * handling rather than two copies of it.
 */
function CaptureImage({
  src,
  alt,
  onDark,
  onImageError,
}: Readonly<{
  src: string;
  alt: string;
  onDark?: boolean;
  onImageError?: () => void;
}>): ReactElement {
  const [hasErrored, setHasErrored] = useState(false);

  if (hasErrored) {
    return <CaptureLoadFailed onDark={onDark} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => {
        setHasErrored(true);
        onImageError?.();
      }}
      style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
    />
  );
}

function CaptureStage({
  box,
  capture,
  label,
  onImageError,
  onPlaybackError,
}: Readonly<{
  box: { width: number; height: number };
  capture: TCapture;
  label: string;
  onImageError?: () => void;
  onPlaybackError?: () => void;
}>): ReactElement {
  if (capture.kind === 'missing') {
    return <MissingCaptureStage width={box.width} height={box.height} />;
  }

  if (!capture.videoSrc) {
    return (
      // Fixed to the stage box rather than sized to the image's own content: a capture whose
      // aspect ratio does not match its box (routine; see `TCaptureEntry`) letterboxes against
      // this background instead of shrinking the box itself down to the picture.
      <Box
        sx={(t: Theme) => ({
          width: box.width,
          height: box.height,
          flex: 'none',
          borderRadius: t.semantic.borderRadius.sm,
          overflow: 'hidden',
          backgroundColor: capture.onDark ? t.palette.common.black : t.palette.background.default,
        })}
      >
        <CaptureImage
          src={capture.src}
          alt={label}
          onDark={capture.onDark}
          onImageError={onImageError}
        />
      </Box>
    );
  }

  // A capture with playback fills the player's stage the same way the video will (100% of the
  // shared box, not sized to the image's own aspect ratio), so swapping still-to-video never
  // changes the stage's footprint. See InlinePlayer for the other half of this fix.
  const poster = (
    <CaptureImage
      src={capture.src}
      alt={label}
      onDark={capture.onDark}
      onImageError={onImageError}
    />
  );

  return (
    <InlinePlayer
      src={capture.videoSrc}
      poster={poster}
      box={box}
      onDark={capture.onDark}
      label={label}
      onError={onPlaybackError}
    />
  );
}

/**
 * The captures column: every capture in the set at its own true size, with one shared note under it.
 *
 * **Takes no `data-testid`, and mints none for its captures.** It used to derive one per capture as
 * `${dataTestId}-${id}`, which is the child-testid proliferation ADR 72 exists to stop, and it did
 * it in component code rather than a story, so every page composing it inherited the pattern. Each
 * capture is instead reached by its own `label`, which the stage puts on the image's `alt` and
 * repeats in the caption beneath it, and the column as a whole through the dialog that contains it.
 */
export default function CaptureViewer({
  captures,
  note,
  onCaptureError,
}: Readonly<TCaptureViewerProps>): ReactElement {
  return (
    <Stack
      sx={(t: Theme) => ({
        flex: 'none',
        width: 'fit-content',
        gap: t.semantic.spacing.sm,
      })}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        sx={(t: Theme) => ({ gap: t.semantic.spacing.base })}
      >
        {captures.map(({ id, label, box, capture }) => (
          // Fixed to the capture's own box width, the same width the stage and the caption below
          // it both use: there is no single column width every capture can share (see
          // `TCaptureEntry`), so the column tracks that capture's own box rather than its content.
          <Stack key={id} sx={{ width: box.width, flex: 'none' }}>
            <CaptureStage
              box={box}
              capture={capture}
              label={label}
              onImageError={onCaptureError ? () => onCaptureError(id) : undefined}
              onPlaybackError={onCaptureError ? () => onCaptureError(id) : undefined}
            />
            {/* Names the capture under the picture. With no tabs left there is nothing else
                telling the reader which of the two they are looking at. */}
            <Typography
              variant="body2"
              sx={(t: Theme) => ({
                width: box.width,
                padding: `${t.semantic.spacing['3xs']} ${t.semantic.spacing['2xs']}`,
                backgroundColor: t.palette.action.hover,
              })}
            >
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          // width: 0 with minWidth: 100% keeps the sentence out of the row's intrinsic width
          // while still letting it fill the row. Without it, a `fit-content` column sizes to the
          // note's max-content, so one long sentence would drag the whole captures column wider
          // than the fixed-width stages it is describing.
          sx={{ width: 0, minWidth: '100%', textWrap: 'pretty' }}
        >
          {note}
        </Typography>
      )}
    </Stack>
  );
}
